import React, { useEffect, useRef, useState } from 'react';
import * as echarts from 'echarts';
import apiService from '../../services/api';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import QuestionDetails from '../Dashboard/QuestionDetails';
import { useAuth } from '../../context/AuthContext';

const { FiLoader, FiShare2, FiTarget } = FiIcons;

const Network = () => {
  const { user } = useAuth();
  const chartRef = useRef(null);
  const chartInstance = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [networkData, setNetworkData] = useState({ nodes: [], links: [], categories: [] });
  const [activeNodeId, setActiveNodeId] = useState(null);
  const [hiddenCategories, setHiddenCategories] = useState([]);
  
  // New state for details
  const [rawElements, setRawElements] = useState([]);
  const [questionsMap, setQuestionsMap] = useState({});
  const [scores, setScores] = useState({});

  // Daten laden beim Mounten
  useEffect(() => {
    fetchData();

    const handleResize = () => {
      chartInstance.current?.resize();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (chartInstance.current) {
        chartInstance.current.dispose();
        chartInstance.current = null;
      }
    };
  }, [user]); // Added user dependency to reload if user changes (e.g. login)

  // Chart initialisieren oder aktualisieren
  useEffect(() => {
    if (!loading && !error && chartRef.current) {
      initChart();
    }
  }, [loading, error, networkData, activeNodeId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [elementsRes, connectionsRes, clustersRes, clusterRelationsRes, questionsRes] = await Promise.all([
        apiService.getElements(),
        apiService.getElementConnections(),
        apiService.getClusters(),
        apiService.getClusterElementRelations(),
        apiService.getQuestions({ limit: 1000 })
      ]);

      const elements = Array.isArray(elementsRes.data) ? elementsRes.data : [];
      const connections = Array.isArray(connectionsRes.data) ? connectionsRes.data : [];
      const clusters = Array.isArray(clustersRes.data) ? clustersRes.data : [];
      const clusterRelations = Array.isArray(clusterRelationsRes.data) ? clusterRelationsRes.data : [];
      const questions = Array.isArray(questionsRes.data) ? questionsRes.data : [];

      setRawElements(elements);

      // Process Questions Map
      const qMap = {};
      questions.forEach(q => {
        if (!qMap[q.element_id]) {
          qMap[q.element_id] = [];
        }
        qMap[q.element_id].push(q);
      });
      setQuestionsMap(qMap);

      // Process Scores if Team exists
      const calculatedScores = {};
      if (user?.teamId) {
        try {
          const teamMembersRes = await apiService.getUserTeamRelations({ team_id: user.teamId, limit: 1000 });
          const members = teamMembersRes.data ? teamMembersRes.data.map(m => m.user_id) : [];
          if (user.id && !members.includes(parseInt(user.id))) {
             members.push(parseInt(user.id));
          }

          if (members.length > 0) {
            const evalsRes = await apiService.getEvaluations({ 'user_id[in]': members.join(','), limit: 2000 });
            const evaluations = evalsRes.data || [];
            
            // Calculate scores per question
            questions.forEach(q => {
               const questionEvals = evaluations.filter(ev => ev.question_id === q.id);
               if (questionEvals.length > 0) {
                 const sum = questionEvals.reduce((acc, curr) => acc + curr.evaluation_value, 0);
                 calculatedScores[q.id] = Math.round((sum / questionEvals.length) * 10) / 10;
               } else {
                 calculatedScores[q.id] = 0;
               }
            });
          }
        } catch (e) {
          console.error("Error fetching scores:", e);
        }
      }
      setScores(calculatedScores);

      // Farben für die Cluster definieren
      const colors = [
        '#5470c6', '#91cc75', '#fac858', '#ee6666', 
        '#73c0de', '#3ba272', '#fc8452', '#9a60b4', '#ea7ccc'
      ];

      // Kategorien erstellen
      let categories = clusters.map((cluster, index) => ({
        name: cluster.title,
        itemStyle: {
          color: colors[index % colors.length]
        }
      }));

      // Mapping von Element ID zu Cluster Index erstellen
      const elementClusterIndex = {};
      clusterRelations.forEach(rel => {
        const clusterIndex = clusters.findIndex(c => c.id === rel.cluster_id);
        if (clusterIndex !== -1) {
          elementClusterIndex[rel.element_id] = clusterIndex;
        }
      });

      // Prüfen, ob es Elemente ohne Cluster gibt
      const hasUnassignedElements = elements.some(el => elementClusterIndex[el.id] === undefined);
      
      // Wenn es nicht zugeordnete Elemente gibt, füge eine entsprechende Kategorie hinzu
      let unassignedCategoryIndex = -1;
      if (hasUnassignedElements) {
        unassignedCategoryIndex = categories.length;
        categories.push({
          name: 'Nicht zugeordnet',
          itemStyle: {
            color: '#e2e8f0', // Helles Grau für unzugeordnete
            borderColor: '#152627',
            borderWidth: 1
          }
        });
      }

      const nodes = elements.map(el => {
        const clusterIndex = elementClusterIndex[el.id];
        const hasCluster = clusterIndex !== undefined;
        
        // Bestimme die Kategorie-ID (Index im categories Array)
        const categoryIndex = hasCluster ? clusterIndex : unassignedCategoryIndex;

        // Styling basierend auf Cluster oder Fallback
        const baseColor = hasCluster ? colors[clusterIndex % colors.length] : '#ffffff';
        const textColor = hasCluster ? '#ffffff' : '#152627';
        const borderColor = hasCluster ? baseColor : '#152627';

        return {
          id: String(el.id),
          name: el.title,
          category: categoryIndex, // Wichtig für die Legenden-Verknüpfung
          symbol: 'rect',
          symbolSize: [120, 40],
          itemStyle: {
            color: baseColor,
            borderColor: borderColor,
            borderWidth: 2,
            borderRadius: 4
          },
          label: {
            show: true,
            color: textColor,
            fontSize: 12,
            fontWeight: 'bold',
            formatter: (params) => {
              return params.name.length > 15 ? params.name.substring(0, 15) + '...' : params.name;
            }
          }
        };
      });

      const links = connections.map(conn => {
        let width = 1;
        let color = '#999';
        let type = 'solid';
        
        switch(conn.weighting) {
          case 'high': 
            width = 4; // Fett
            color = '#152627'; 
            type = 'solid';
            break;
          case 'middle': 
            width = 2; 
            color = '#64748b'; 
            type = 'dashed'; // Gestrichelt
            break;
          case 'low': 
            width = 2; 
            color = '#94a3b8'; 
            type = 'dotted'; // Gepunktet
            break;
          default: 
            width = 1;
            type = 'solid';
        }

        return {
          source: String(conn.element_id),
          target: String(conn.connection_element_id),
          lineStyle: { width, color, type, curveness: 0.1 }
        };
      });

      setNetworkData({ nodes, links, categories });
    } catch (err) {
      console.error('Error fetching network data:', err);
      setError('Fehler beim Laden des Netzwerks.');
    } finally {
      setLoading(false);
    }
  };

  const toggleCategory = (categoryName) => {
    if (chartInstance.current) {
        chartInstance.current.dispatchAction({
            type: 'legendToggleSelect',
            name: categoryName
        });
        setHiddenCategories(prev => {
            if (prev.includes(categoryName)) {
                return prev.filter(c => c !== categoryName);
            } else {
                return [...prev, categoryName];
            }
        });
    }
  };

  const initChart = () => {
    if (!chartRef.current) return;

    if (!chartInstance.current) {
      chartInstance.current = echarts.init(chartRef.current);
      
      // Event Listener für Klick auf Nodes
      chartInstance.current.on('click', (params) => {
        if (params.dataType === 'node') {
          const clickedId = params.data.id;
          setActiveNodeId(prev => prev === clickedId ? null : clickedId);
        }
      });

      // Event Listener für Klick auf den Hintergrund (zum Zurücksetzen)
      chartInstance.current.getZr().on('click', (params) => {
        if (!params.target) {
          setActiveNodeId(null);
        }
      });
    }

    if (networkData.nodes.length === 0) {
      chartInstance.current.setOption({
        title: {
          text: 'Keine Elemente gefunden',
          left: 'center',
          top: 'center',
          textStyle: { color: '#666' }
        }
      });
      return;
    }

    // --- Logik für das Ausblenden (Dimmen) ---
    let displayNodes = networkData.nodes;
    let displayLinks = networkData.links;

    if (activeNodeId) {
      // 1. Finde alle verbundenen Nodes (Nachbarn)
      const connectedNodeIds = new Set();
      connectedNodeIds.add(activeNodeId);

      networkData.links.forEach(link => {
        if (link.source === activeNodeId) connectedNodeIds.add(link.target);
        if (link.target === activeNodeId) connectedNodeIds.add(link.source);
      });

      // 2. Nodes verarbeiten: Nicht verbundene Nodes dimmen
      displayNodes = networkData.nodes.map(node => {
        const isConnected = connectedNodeIds.has(node.id);
        if (isConnected) {
          return node; // Originalzustand
        } else {
          // Gedimmter Zustand
          return {
            ...node,
            itemStyle: {
              ...node.itemStyle,
              opacity: 0.1,
              borderColor: 'transparent' // Optional: Rand entfernen für sauberen Look
            },
            label: {
              ...node.label,
              opacity: 0.1
            }
          };
        }
      });

      // 3. Links verarbeiten: Nicht verbundene Links stark dimmen
      displayLinks = networkData.links.map(link => {
        const isConnected = link.source === activeNodeId || link.target === activeNodeId;
        if (isConnected) {
          return link;
        } else {
          return {
            ...link,
            lineStyle: {
              ...link.lineStyle,
              opacity: 0.05
            }
          };
        }
      });
    }

    const option = {
      title: {
        show: false
      },
      tooltip: {
        trigger: 'item',
        formatter: (params) => {
          if (params.dataType === 'node') {
            return `<b>Element:</b> ${params.data.name}<br/>${params.data.category !== undefined && networkData.categories[params.data.category] ? 'Cluster: ' + networkData.categories[params.data.category].name : 'Kein Cluster'}`;
          } else if (params.dataType === 'edge') {
            return `<b>Verbindung:</b><br/>Von: ${params.data.source}<br/>Zu: ${params.data.target}`;
          }
        }
      },
      legend: {
        show: false, // Native Legende ausblenden
        data: networkData.categories.map(c => c.name),
        selectedMode: true
      },
      series: [
        {
          type: 'graph',
          layout: 'force',
          data: displayNodes,
          links: displayLinks,
          categories: networkData.categories,
          roam: true,
          draggable: true,
          zoom: 0.6, // Initiale Zoom-Stufe angepasst (0.6 statt 1)
          center: ['50%', '50%'], // Zentrierung explizit gesetzt
          edgeSymbol: ['none', 'arrow'],
          edgeSymbolSize: [4, 10],
          force: {
            repulsion: 1000,
            edgeLength: 200,
            gravity: 0.1
          },
          emphasis: {
            // focus: 'adjacency' ist explizit entfernt, um unerwünschte Hover-Effekte zu vermeiden
            lineStyle: { width: 4 }
          }
        }
      ]
    };

    chartInstance.current.setOption(option);
  };

  // Helper to find selected element object
  const getSelectedElement = () => {
    if (!activeNodeId) return null;
    return rawElements.find(el => String(el.id) === activeNodeId);
  };
  
  const selectedElement = getSelectedElement();

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <SafeIcon icon={FiLoader} className="mx-auto h-8 w-8 animate-spin text-brand-primary" />
          <p className="mt-2 text-sm text-gray-500">Lade Netzwerk...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8 text-center text-red-600">
        <p>{error}</p>
        <button 
          onClick={fetchData}
          className="mt-4 px-4 py-2 bg-brand-primary text-white rounded hover:bg-brand-secondary"
        >
          Erneut versuchen
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(90vh-64px)] bg-gray-50 p-6">
      <div className="mb-6 flex-shrink-0">
        <h1 className="text-2xl font-bold text-brand-dark flex items-center">
          <SafeIcon icon={FiShare2} className="w-7 h-7 mr-3 text-brand-secondary" />
          Netzwerk
        </h1>
        <p className="text-gray-600 mt-2">
          Erkennen Sie Zusammenhänge zwischen den Elementen über das Diagramm. Mit dem Klick auf eines der Elemente sehen Sie alle Zusammenhänge sowie die aktuellen Bewertungen im Team.
        </p>
      </div>
      
      <div className="flex-1 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden relative grid grid-cols-1 lg:grid-cols-3">
        <div className="lg:col-span-2 relative h-full border-b lg:border-b-0 lg:border-r border-gray-200">
          <div ref={chartRef} className="w-full h-full" />
          
          {/* Custom Cluster Legend Top Right */}
          <div className="absolute top-4 right-4 bg-white/90 p-3 rounded-lg shadow border border-gray-200 text-xs max-w-[220px] max-h-[400px] overflow-y-auto z-10">
            <div className="font-semibold mb-2 text-gray-800">Klicke auf Cluster um sie zu (de-)aktivieren</div>
            <div className="space-y-1">
              {networkData.categories.map((cat, idx) => (
                <div 
                  key={idx} 
                  onClick={() => toggleCategory(cat.name)}
                  className={`flex items-center cursor-pointer p-1.5 rounded hover:bg-gray-100 transition-all duration-200 ${hiddenCategories.includes(cat.name) ? 'opacity-40 grayscale bg-gray-50' : ''}`}
                >
                  <span 
                    className="w-3 h-3 rounded-full mr-2 shadow-sm flex-shrink-0"
                    style={{ backgroundColor: cat.itemStyle.color }} 
                  />
                  <span className="truncate">{cat.name}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Connection Legend Bottom Right */}
          <div className="absolute bottom-4 right-4 bg-white/90 p-3 rounded-lg shadow border border-gray-200 text-xs pointer-events-none z-10">
            <div className="font-semibold mb-2">Legende (Verbindungsstärke):</div>
            <div className="flex items-center mb-1">
              <div className="w-8 border-t-4 border-solid border-[#152627] mr-2"></div> Stark (High)
            </div>
            <div className="flex items-center mb-1">
              <div className="w-8 border-t-2 border-dashed border-[#64748b] mr-2"></div> Mittel (Middle)
            </div>
            <div className="flex items-center">
              <div className="w-8 border-t-2 border-dotted border-[#94a3b8] mr-2"></div> Schwach (Low)
            </div>
          </div>
        </div>

        {/* Detail View Side Panel */}
        <div className="lg:col-span-1 bg-gray-50/30 flex flex-col h-full overflow-hidden">
          <div className="p-6 border-b border-gray-100 flex-shrink-0 bg-white">
            <h2 className="text-lg font-semibold text-brand-dark">
              {selectedElement ? (
                <span className="flex items-center text-brand-secondary">
                  <SafeIcon icon={FiTarget} className="w-5 h-5 mr-2" />
                  {selectedElement.title}
                </span>
              ) : 'Detailansicht'}
            </h2>
          </div>
          
          <div className="flex-grow overflow-y-auto p-6 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
            {selectedElement ? (
              <QuestionDetails 
                element={selectedElement}
                questions={questionsMap[selectedElement.id]}
                scores={scores}
              />
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-gray-400">
                <SafeIcon icon={FiTarget} className="w-16 h-16 mb-4 opacity-20" />
                <p className="text-center">Wählen Sie ein Element im Netzdiagramm aus,<br/>um detaillierte Fragen-Auswertungen zu sehen.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Network;